'use client';

import * as React from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { ChevronDown } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { generateTestId } from '@/utils/shared/generateTestIds';

export type SwitchOption = {
  id: string;
  label: string;
  checked: boolean;
  disabled?: boolean;
};

export type SwitchGroup = {
  title?: string;
  options: SwitchOption[];
  requireSelection?: boolean;
};

type SwitchropdownProps = {
  triggerText?: string | React.ReactNode;
  triggerClassName?: string;
  triggerSize?: 'sm' | 'default' | 'icon' | 'xs' | 'lg' | null | undefined;
  groups: SwitchGroup[];
  triggerVariant?: any;
  width?: string;
  requireSelection?: boolean;
  placementSide?: 'top' | 'bottom' | 'left' | 'right';
  placementAlign?: 'start' | 'center' | 'end';
  placementOffset?: number;
  dataTestid?: string;
  onChange?: (optionId: string, checked: boolean) => void;
  onSelectionChange?: (selected: Record<string, SwitchOption[]>) => void;
};

export function SwitchDropdown({
  triggerText = 'Select Options',
  triggerClassName = '',
  groups,
  width = 'w-full min-w-56',
  triggerSize = 'sm',
  placementSide,
  placementAlign,
  requireSelection = false,
  triggerVariant = 'outline',
  dataTestid,
  onChange,
  onSelectionChange,
}: SwitchropdownProps) {
  const [options, setOptions] = React.useState<Record<string, SwitchOption>>({});
  const [groupMap, setGroupMap] = React.useState<Record<string, string>>({});
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    const initialOptions: Record<string, SwitchOption> = {};
    const initialGroupMap: Record<string, string> = {};

    groups.forEach((group, groupIndex) => {
      group.options.forEach((option) => {
        initialOptions[option.id] = { ...option };
        initialGroupMap[option.id] = groupIndex.toString();
      });
    });

    setOptions(initialOptions);
    setGroupMap(initialGroupMap);
  }, [groups]);

  const wouldViolateRequirement = (optionId: string, newCheckedState: boolean): boolean => {
    if (newCheckedState) return false;

    const groupIndex = groupMap[optionId];
    if (groupIndex === undefined) return false;

    const group = groups[Number.parseInt(groupIndex)];
    const isGroupRestricted = requireSelection !== undefined ? requireSelection : group.requireSelection;

    if (!isGroupRestricted) return false;

    let checkedCount = 0;
    group.options.forEach((option) => {
      if (option.id !== optionId && options[option.id]?.checked) {
        checkedCount++;
      }
    });

    return checkedCount === 0;
  };

  const handleCheckedChange = (optionId: string, checked: boolean) => {
    if (wouldViolateRequirement(optionId, checked)) return;
    setOptions((prev) => {
      const newOptions = {
        ...prev,
        [optionId]: {
          ...prev[optionId],
          checked,
        },
      };

      onChange?.(optionId, checked);

      if (onSelectionChange) {
        const selectedGroups: Record<string, SwitchOption[]> = {};
        Object.entries(newOptions).forEach(([id, opt]) => {
          if (opt.checked) {
            const groupIndex = groupMap[id];
            if (!selectedGroups[groupIndex]) {
              selectedGroups[groupIndex] = [];
            }
            selectedGroups[groupIndex].push(opt);
          }
        });

        onSelectionChange(selectedGroups);
      }

      return newOptions;
    });
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild className={open && triggerVariant === 'ghost' ? 'bg-[#01152B29]' : ''}>
        <Button
          size={triggerSize}
          variant={triggerVariant}
          className={cn('flex items-center justify-between gap-2', triggerClassName)}
          {...(dataTestid ? { 'data-testid': dataTestid } : {})}
        >
          <span className="text-sm">{triggerText}</span>
          <ChevronDown className={cn('h-4 w-4 transition-transform duration-200', open && 'rotate-180')} />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent className={width} align={placementAlign} side={placementSide}>
        {groups.map((group, groupIndex) => {
          const isGroupRestricted = requireSelection !== undefined ? requireSelection : group.requireSelection;
          const checkedCount = group.options.reduce((count, option) => {
            return options[option.id]?.checked ? count + 1 : count;
          }, 0);

          return (
            <React.Fragment key={groupIndex}>
              {group.title && (
                <>
                  <DropdownMenuLabel className="flex justify-between items-center ">
                    {group.title}
                    {isGroupRestricted && (
                      <span className="text-xs text-muted-foreground ml-2">At least one required</span>
                    )}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                </>
              )}

              {group.options.map((option) => {
                const currentOption = options[option.id];
                if (!currentOption) return null;

                const isLastCheckedItem = isGroupRestricted && currentOption.checked && checkedCount === 1;

                return (
                  <DropdownMenuItem
                    key={option.id}
                    onSelect={(e) => e.preventDefault()}
                    onClick={() => {
                      if (!currentOption.disabled && !isLastCheckedItem) {
                        handleCheckedChange(option.id, !currentOption.checked);
                      }
                    }}
                    className={cn(
                      'flex items-center justify-between gap-2 whitespace-nowrap font-medium cursor-pointer',
                      (currentOption.disabled || isLastCheckedItem) &&
                        'cursor-not-allowed opacity-60 cursor-not-allowed'
                    )}
                    data-testid={`menu_item_${generateTestId(option.label)}`}
                  >
                    <span
                      className={cn(
                        'text-sm',
                        (currentOption.disabled || isLastCheckedItem) && 'text-muted-foreground'
                      )}
                      title={isLastCheckedItem ? 'At least one option must be selected' : ''}
                    >
                      {option.label}
                      {isLastCheckedItem && (
                        <span className="text-xs text-muted-foreground ml-2 font-medium">(required)</span>
                      )}
                    </span>
                    <Switch checked={currentOption.checked} disabled={false} />
                  </DropdownMenuItem>
                );
              })}

              {groupIndex < groups.length - 1 && <DropdownMenuSeparator />}
            </React.Fragment>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
